import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

interface HeroContentProps {
  title: string;
  subtitle: string;
  description: string;
  primaryCTA?: {
    text: string;
    link: string;
    isExternal?: boolean;
  };
  secondaryCTA?: {
    text: string;
    link: string;
    isExternal?: boolean;
  };
  isHomepage?: boolean;
  portraitMode?: boolean;
  slogan?: string;
  textEnhancementClass?: string;
  headingLevel?: 'h1' | 'h2' | 'h3';
}

const HeroContent: React.FC<HeroContentProps> = ({
  title,
  subtitle,
  description,
  primaryCTA,
  secondaryCTA,
  isHomepage = false,
  portraitMode = false,
  slogan,
  textEnhancementClass = "",
  headingLevel = 'h1'
}) => {
  const renderCTALink = (cta: { text: string; link: string; isExternal?: boolean }, isPrimary: boolean) => {
    // Determine button classes based on whether it's primary or secondary
    const buttonClassNames = isPrimary
      ? "bg-primary text-white hover:bg-primary-700 rounded-full inline-flex items-center justify-center shadow-md hover:shadow-lg transition-all px-6 py-3"
      : "bg-transparent border border-primary/70 text-primary hover:bg-primary/5 rounded-full inline-flex items-center justify-center shadow-md hover:shadow-lg transition-all px-5 py-2.5";

    if (cta.isExternal) {
      return (
        <a
          href={cta.link}
          className={buttonClassNames}
          target="_blank"
          rel="noopener noreferrer"
        >
          {cta.text}
          <ArrowRight className="ml-2 h-4 w-4" />
        </a>
      );
    }

    return (
      <Link
        to={cta.link}
        className={buttonClassNames}
      >
        {cta.text}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Link>
    );
  };

  // Heading: Playfair Display via font-heading, Deep-Sage color
  const heroTitleClass = `${portraitMode ? 'text-left' : 'text-center'} ${isHomepage ? 'text-3xl md:text-5xl' : 'text-2xl md:text-4xl'} font-heading font-bold leading-tight mb-4 ${textEnhancementClass} text-deep-sage`;

  const descriptionClass = `mb-6 ${portraitMode ? 'text-left' : 'text-center'} ${textEnhancementClass} ${isHomepage ? 'text-soft-charcoal max-w-2xl mx-auto text-lg leading-relaxed' : 'text-soft-charcoal text-lg leading-relaxed max-w-2xl mx-auto'}`;

  const sloganClass = isHomepage
    ? 'text-xl md:text-2xl mb-4 text-left font-semibold text-deep-sage/80'
    : 'text-xl md:text-2xl mb-4 text-left text-soft-charcoal';
  const HeadingTag = headingLevel as keyof JSX.IntrinsicElements;

  return (
    <div className={`${portraitMode ? 'lg:col-span-6' : 'w-full max-w-4xl mx-auto text-center'}`}>
      {subtitle && (
        <div className="inline-block bg-sage-100/80 text-deep-sage font-medium mb-4 px-4 py-2 rounded-full text-sm backdrop-blur-sm shadow-sm border border-sage-200/60">
          {subtitle}
        </div>
      )}
      
      <HeadingTag className={heroTitleClass} data-speakable="headline">
        {title}
      </HeadingTag>
      
      {slogan && (
        <p className={sloganClass}>
          {slogan}
        </p>
      )}
      
      <p className={descriptionClass} data-speakable="description">
        {description}
      </p>
      
      {(primaryCTA || secondaryCTA) && (
        <div className={`flex flex-col sm:flex-row gap-4 mt-8 ${portraitMode ? 'justify-start' : 'justify-center'}`}>
          {primaryCTA && renderCTALink(primaryCTA, true)}
          {secondaryCTA && renderCTALink(secondaryCTA, false)}
        </div>
      )}
    </div>
  );
};

export default HeroContent;